import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ApiService } from '../../../../core/api-service';
import { FeedbackService } from '../../../../core/feedback.service';
import { catchError, debounceTime, filter, map, mergeMap, reduce, retry, switchMap, takeUntil } from 'rxjs/operators';
import { combineLatestWith, EMPTY, forkJoin, from, Observable, of, Subject, tap, throwError, timer } from 'rxjs';
import { ApiResponse } from '../../../../models/api-response';
import { HttpErrorResponse } from '@angular/common/http';
import { ColumnNote } from '../../../../models/locations-models/column-note';
import { getChangeableGmbFields, getFullNameForFieldID } from '../../location-utils';
import { LocalStorageKeys } from '../../enums/local-storage-keys';
import { startOfDay, subDays } from 'date-fns';
import { getFromLocalStorage, setToLocalStorage } from '../../../../core/utilities';
import * as locationListSelectors from './location-list-selectors';
import { selectLocations } from './location-list-selectors';
import { selectAccountID, selectLastManagedLocationCountRefreshTimestamp, selectManagedLocationCount } from '../../../../store/global/core-selectors';
import * as globalActions from '../../../../store/global/core-actions';
import { DATA_STALE_AFTER_HR, MAX_LOCATION_BATCH, MAX_PARALLEL_REQUESTS, MAX_RETRIES_PER_PAGE_SIZE, MIN_BATCH_SIZE, RETRY_DELAY_MS } from '../../constants';
import { MatDialog } from '@angular/material/dialog';
import { BlacklistFieldsPopupComponent } from '../popups/blacklist-fields-popup/blacklist-fields-popup.component';
import { OngoingActionsEnum } from '../../enums/ongoing-actions';
import { GetGbpUpdatesPopupComponent } from '../popups/get-gbp-updates-popup/get-gbp-updates-popup.component';
import { Location } from '../../../../models/locations-models/location';
import { LocationsSkeletonService } from '../../locations-skeleton.service';
import { ActiveDataConnectionsActions, AddressTranslationActions, BlacklistingActions, BulkValidationActions, CCCompletionActions, ColumnNotesActions, CountryListActions, EditGbpConnectionsActions, EditLocationActions, GbpLocationGroupsActions, LoadingSpinnerActions, LocationsLifecycleActions, LocationsRefreshActions, OngoingActions, RefreshGbpConnectionsActions, RowActions, SyncActions, ValidationHistoryActions } from './location-list-actions';
import { CCActionBarActions, CCListRefreshActions, markAllCCListsAsStale } from '../../cc-list/state/cc-list-actions';
import { DatasourceSyncStatus } from '../../../../models/datasource-sync-details';
import { selectIsCcOn } from '../../state/location-skeleton-combined-selectors';
import { TranslateService } from '@ngx-translate/core';
import * as skeletonSelectors from '../../state/locations-skeleton-selectors';
import * as mapSelectors from '../../map/state/map-selectors';
import { BottomPanelTabs, RightPanelTabs } from '../../locations-skeleton.component';
import { CoordinateHistory } from '../../../../models/coordinate-history';


@Injectable()
export class LocationListEffects {
  constructor(
      private store: Store,
      private actions$: Actions,
      private apiService: ApiService,
      private feedbackService: FeedbackService,
      private dialog: MatDialog,
      private translate: TranslateService,
      private skeletonService: LocationsSkeletonService) {
  }

  updateListData$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(LocationsRefreshActions.getEntireLocationList, LocationsLifecycleActions.locationsComponentInitiated, LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => [
          this.store.select(selectAccountID),
          this.store.select(selectLocations),
          this.store.select(selectLastManagedLocationCountRefreshTimestamp),
          this.store.select(selectManagedLocationCount),
        ]),
        switchMap(([action, accountId, locations, lastCountRefreshTimestamp, count]) => {
          if (action.type === LocationsLifecycleActions.locationsComponentDestroyed.type ||
              (action.type === LocationsLifecycleActions.locationsComponentInitiated.type && locations.length)) {
            return EMPTY;
          }

          // Check if count is stale when user explicitly refreshes
          const isUserRefresh = action.type === LocationsRefreshActions.getEntireLocationList.type;
          const isCountStale = lastCountRefreshTimestamp &&
              ((new Date().getTime() - lastCountRefreshTimestamp) / (1000 * 60 * 60)) >= DATA_STALE_AFTER_HR;

          // If user refreshes and count is stale, refresh count first
          if (isUserRefresh && isCountStale) {
            return of(globalActions.ManagedLocationCountActions.refreshManagedLocationCount());
          }

          if (count !== null) {
            return this.loadLocationsInParallel(accountId, count);
          }

          return of(globalActions.ManagedLocationCountActions.loadManagedLocationCount());
        }),
    );
  });

  loadLocationsAfterCountLoad$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(globalActions.ManagedLocationCountActions.loadManagedLocationCountSuccess),
        concatLatestFrom(() => [
          this.store.select(selectAccountID),
        ]),
        switchMap(([action, accountId]) => {
          return this.loadLocationsInParallel(accountId, action.count);
        }),
        catchError((err: HttpErrorResponse) => {
          console.error('Error in loadLocationsAfterCountLoad$:', err);
          this.feedbackService.showAlertSnackbar({
            data: {
              userFriendlyMessage: 'Could not load locations after loading count.',
              detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
            }, isAddressListSnackbar: true,
          }, err?.status);
          return of(LoadingSpinnerActions.stopLoadingSpinner());
        }),
    );
  });

  // This effect triggers location loading after count refresh is complete
  loadLocationsAfterCountRefresh$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(globalActions.ManagedLocationCountActions.refreshManagedLocationCountSuccess),
        concatLatestFrom(() => [
          this.store.select(selectAccountID),
        ]),
        switchMap(([action, accountId]) => {
          return this.loadLocationsInParallel(accountId, action.count);
        }),
        catchError((err: HttpErrorResponse) => {
          console.error('Error in loadLocationsAfterCountRefresh$:', err);
          this.feedbackService.showAlertSnackbar({
            data: {
              userFriendlyMessage: 'Could not load locations after refreshing count.',
              detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
            }, isAddressListSnackbar: true,
          }, err?.status);
          return of(LoadingSpinnerActions.stopLoadingSpinner());
        }),
    );
  });

  private createRetriableLocationRequest(accountId: number, pageIndex: number, pageSize: number): Observable<Location[]> {
    return this.apiService.getLocations(accountId, pageSize, pageIndex).pipe(
        map(res => res.result?.locations || []),
        retry({
          count: MAX_RETRIES_PER_PAGE_SIZE,
          delay: (error, retryCount) => {
            if (error instanceof HttpErrorResponse && error.status >= 500) {
              return throwError(() => error);
            }
            return timer(RETRY_DELAY_MS * (2 ** (retryCount - 1)));
          },
        }),
        catchError(err => {
          if (pageSize > MIN_BATCH_SIZE) {
            const newPageSize = Math.max(MIN_BATCH_SIZE, Math.floor(pageSize / 2));
            const splitFactor = Math.ceil(pageSize / newPageSize);
            const newStartingPageIndex = Math.floor(((pageIndex - 1) * pageSize) / newPageSize) + 1;
            const newPageIndexes = Array.from({ length: splitFactor }, (_, i) => newStartingPageIndex + i);

            console.warn(`Request for page ${pageIndex} (size ${pageSize}) failed. Splitting into ${splitFactor} requests of size ${newPageSize}.`);

            const smallerRequests$ = newPageIndexes.map(newIndex =>
                this.createRetriableLocationRequest(accountId, newIndex, newPageSize),
            );

            return forkJoin(smallerRequests$).pipe(
                map(arrayOfLocationArrays => arrayOfLocationArrays.flat()),
            );
          } else {
            console.error(`Failed to load locations for page ${pageIndex} at minimum batch size ${MIN_BATCH_SIZE}. Aborting operation.`);
            return throwError(() => new Error(`Failed to load page ${pageIndex} at min size.`));
          }
        }),
    );
  }

  /**
   * Formats duration in milliseconds to a human-readable string
   */
  private formatDuration(durationMs: number): string {
    if (durationMs < 60000) {
      return (durationMs / 1000).toFixed(2) + 's';
    } else {
      const minutes = Math.floor(durationMs / 60000);
      const seconds = ((durationMs % 60000) / 1000).toFixed(0);
      return `${minutes}min ${seconds}s`;
    }
  }

  /**
   * Loads locations in parallel using the shared logic
   */
  private loadLocationsInParallel(accountId: number, totalLocations: number): Observable<any> {
    if (totalLocations === 0) {
      return of(LocationsRefreshActions.getEntireLocationListSuccess({
        locations: [],
        lastSuccessfulPageSize: MAX_LOCATION_BATCH,
      }));
    }

    const startTime = Date.now();
    const totalPages = Math.ceil(totalLocations / MAX_LOCATION_BATCH);
    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

    return from(pageNumbers).pipe(
        mergeMap(pageIndex =>
            this.createRetriableLocationRequest(accountId, pageIndex, MAX_LOCATION_BATCH), MAX_PARALLEL_REQUESTS),
        reduce((acc: Location[], val: Location[]) => [...acc, ...val], []),
        tap(allLocations => {
          const durationMs = Date.now() - startTime;
          const durationFormatted = this.formatDuration(durationMs);
          console.info(`Fetching ${allLocations.length} locations took ${durationFormatted}.`);
        }),
        mergeMap(allLocations => [
          LocationsRefreshActions.getEntireLocationListSuccess({
            locations: allLocations,
            lastSuccessfulPageSize: MAX_LOCATION_BATCH,
          }),
          markAllCCListsAsStale(),
        ]),
        catchError(err => {
          console.error('Location loading aborted:', err);
          this.feedbackService.showAlertSnackbar({
            data: {
              userFriendlyMessage: 'Loading addresses failed. Please try again later.',
              detailedInfoFromBE: [err?.message || 'An unknown error occurred.'],
            }, isAddressListSnackbar: true,
          }, 0);
          return of(LoadingSpinnerActions.stopLoadingSpinner());
        }),
    );
  }

  clearLocationsStateOnAccountSwitch$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(globalActions.updateSelectedAccount),
        map(() => LocationsLifecycleActions.clearLocationsState()),
    );
  });

  focusRow$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(RowActions.userFocusRow),
        concatLatestFrom(() => [this.store.select(locationListSelectors.selectFocusedRowId)]),
        switchMap(([action, focusedRowId]) => {
          // we want to run the check only when there is no focused row or user focused a different row
          return !focusedRowId || focusedRowId !== action.id ?
              this.skeletonService.canDestroyExpansionPanelContainers$()
                  .pipe(map(canDestroy => {
                    if (canDestroy) {
                      return RowActions.updateFocusedRow({ id: action.id });
                    } else return globalActions.noop();
                  })) :
              of(RowActions.updateFocusedRow({ id: action.id }));
        }),
    );
  });

  updateDataInBls$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(EditLocationActions.editLocationData, LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => [this.store.select(selectAccountID), this.store.select(selectIsCcOn)]),
        // merge map here since we want to fire parallel requests in case the user is
        // quickly editing comment fields in the table
        mergeMap(([action, accountId, ccActive]) => action.type === LocationsLifecycleActions.locationsComponentDestroyed.type ? EMPTY :
            this.apiService.updateDataInBLS(accountId, action.location)
                .pipe(
                    mergeMap((response: ApiResponse<Location>) => {
                      const returnActions: any[] = [
                        EditLocationActions.editLocationDataSuccess({
                          location: response.result,
                          singleField: action.singleField,
                        }),
                      ];

                      // Update cc list since bls update changes imported location key on the BE
                      // which COULD affect CC candidates.
                      // When updating the comment field directly in location list, do not trigger
                      // CC list update since the call is costly and a change in comment will not
                      // affect CC candidates
                      if (ccActive && EditLocationActions.editLocationData.type === action.type && action.updateCCList) {
                        returnActions.push(markAllCCListsAsStale()); // mark all cc lists as stale for now
                        returnActions.push(CCListRefreshActions.updateCCList()); // update just the list for the currently focused location (will unmark that list as well)
                      }

                      return returnActions;
                    }),
                    catchError((err: HttpErrorResponse) => {
                      this.feedbackService.showAlertSnackbar({
                        data: {
                          userFriendlyMessage: 'Updating data in BLS failed',
                          detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                        }, isAddressListSnackbar: true,
                      }, err?.status);

                      return of(EditLocationActions.editLocationDataError({
                        location: action.location,
                        singleField: action.singleField,
                      }));
                    }),
                ),
        ),
    );
  });

  updateClaimCleanCompletion$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CCCompletionActions.updateCcCompletionTimestamp, LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type === LocationsLifecycleActions.locationsComponentDestroyed.type ? EMPTY :
            this.apiService.updateClaimCleanCompletion(accountId, action.location.id, action.completed)
                .pipe(
                    map((response) => {
                      const updatedLocation = response.result;

                      this.feedbackService.showSuccessSnackbar({
                        data: {
                          userFriendlyMessage: 'Updating CC Completion was successful!',
                        }, isAddressListSnackbar: true,
                      });

                      return CCCompletionActions.updateCcCompletionTimestampSuccess({ location: updatedLocation });
                    }),
                    catchError((err: HttpErrorResponse) => {
                      this.feedbackService.showAlertSnackbar({
                        data: {
                          userFriendlyMessage: 'Updating CC Completion failed',
                          detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                        }, isAddressListSnackbar: true,
                      }, err?.status);

                      return of(CCCompletionActions.updateCcCompletionTimestampError({ location: action.location }));
                    }),
                ),
        ),
    );
  });

  /**
   * Sets the currently selected claiming location group
   */
  setDefaultClaimingLocationGroup = createEffect(() => {
    return this.actions$.pipe(
        ofType(GbpLocationGroupsActions.updateGbpLocationGroupsSuccess, LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) =>
            action.type !== LocationsLifecycleActions.locationsComponentDestroyed.type
                ? this.apiService.getClaimingLocationGroupId(accountId.toString())
                    .pipe(map((claimingLocationGroupId: ApiResponse<string>) => {
                          const defaultClaimingLocationGroup = action.gbpLocationGroups.filter(grp => grp.accountName?.length)
                              .find(grp => grp.gmbAccountId === claimingLocationGroupId.result);

                          return CCActionBarActions.updateCCLocationGroup({ ccLocationGroup: defaultClaimingLocationGroup });
                        }),
                        catchError((err: HttpErrorResponse) => {
                          this.feedbackService.showAlertSnackbar({
                            data: {
                              userFriendlyMessage: 'Getting location group for Claim & Clean failed.',
                              detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                            }, isAddressListSnackbar: true,
                          }, err?.status);
                          return of(LoadingSpinnerActions.stopLoadingSpinner());
                        }),
                    ) : EMPTY,
        ),
    );
  });

  updateGbpLocationGroups$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(GbpLocationGroupsActions.updateGbpLocationGroups,
            LocationsLifecycleActions.locationsComponentInitiated,
            LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type === LocationsLifecycleActions.locationsComponentDestroyed.type ?
            EMPTY : this.apiService.getGbpGroups(accountId)
                .pipe(
                    mergeMap((response: ApiResponse) =>
                        [GbpLocationGroupsActions.updateGbpLocationGroupsSuccess({
                          gbpLocationGroups:
                              response?.result?.gmbLocationGroups?.map(gmbGroup => ({
                                accountName: gmbGroup.accountName,
                                gmbAccountId: gmbGroup.gmbAccountId,
                              })),
                        }),
                          OngoingActions.removeOngoingAction({
                            ongoingAction: OngoingActionsEnum.UPDATE_GBP_GROUPS,
                          })],
                    ),
                    catchError((err: HttpErrorResponse) => {
                      this.feedbackService.showAlertSnackbar({
                        data: {
                          userFriendlyMessage: 'Getting GBP Accounts failed',
                          detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                        }, isAddressListSnackbar: true,
                      }, err?.status);
                      return of(EMPTY).pipe(mergeMap(() => [
                            OngoingActions.removeOngoingAction({
                              ongoingAction: OngoingActionsEnum.UPDATE_GBP_GROUPS,
                            }),
                            GbpLocationGroupsActions.updateGbpLocationGroupsError(),
                          ],
                      ));
                    }),
                ),
        ),
    );
  });

  getLocationAddressTranslation$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(AddressTranslationActions.getAddressTranslation,
            LocationsLifecycleActions.locationsComponentDestroyed),
        switchMap((action) => action.type !== LocationsLifecycleActions.locationsComponentDestroyed.type ? this.apiService.getLocationAddressTranslation(action.id)
            .pipe(
                map((res) => AddressTranslationActions
                    .getAddressTranslationSuccess({ addressTranslation: res?.result })),
                catchError((err: HttpErrorResponse) => {
                  this.feedbackService.showAlertSnackbar({
                    data: {
                      userFriendlyMessage: 'Address translation failed.',
                      detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                    },
                  }, err?.status);
                  return of(AddressTranslationActions.getAddressTranslationFail({ id: action.id }));
                }),
            ) : EMPTY,
        ),
    );
  });

  /**
   * Listens to actions that might have changed original location data. If there is a cached translation obj
   * for the respective location, fetch a fresh one!
   * Since at this point, the location data is already up-to-date, there is no way to diff-check
   * in order to optimize the calls...
   */
  refetchTranslationOnPotentialDataChange$ = createEffect(() => {
    return this.actions$.pipe(
        // we do not re-fetch translation after each data update,
        // only after location has been edited (or user accepted/rejected a difference) by the current user.
        ofType(
            EditLocationActions.editLocationDataSuccess, // dispatched after location edit was successful
            LocationsRefreshActions.patchLocation), // dispatched after accepting/rejecting difference (currently)
        concatLatestFrom((action) => this.store.select(locationListSelectors.selectAddressTranslation(action.location.id))),
        filter(([_, addressTranslation]) => !!addressTranslation), // re-fetch only if there's a cache hit
        map(([action, _]) => {
          return AddressTranslationActions.getAddressTranslation({ id: action.location.id });
        }),
    );
  });

  loadActiveDataConnections$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ActiveDataConnectionsActions.loadActiveDataConnections,
            LocationsLifecycleActions.locationsComponentInitiated,
            LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountID]) => action.type === LocationsLifecycleActions.locationsComponentDestroyed.type ? EMPTY :
            this.apiService.getDataSourceConfig(accountID)
                .pipe(
                    map((res: ApiResponse<Array<string>>) =>
                        ActiveDataConnectionsActions.loadActiveDataConnectionsSuccess({ activeDataConnections: res.result }),
                    ),
                    catchError((err: HttpErrorResponse) => {
                      this.feedbackService.showAlertSnackbar({
                        data: {
                          userFriendlyMessage: 'Active JSON data connections couldn\'t be shown.',
                          detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                        }, isAddressListSnackbar: true,
                      }, err?.status);
                      return of(LoadingSpinnerActions.stopLoadingSpinner());
                    }),
                ),
        ),
    );
  });

  updateCountryList$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(CountryListActions.updateCountryList,
            LocationsLifecycleActions.locationsComponentInitiated,
            LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type === LocationsLifecycleActions.locationsComponentDestroyed.type ? EMPTY :
            this.apiService.getCountryListISO2(accountId)
                .pipe(
                    map((response: ApiResponse<string[]>) =>
                        CountryListActions.updateCountryListSuccess({
                          countryList: response?.result || null,
                        }),
                    ),
                    catchError((err: HttpErrorResponse) => {
                      this.feedbackService.showAlertSnackbar({
                        data: {
                          userFriendlyMessage: 'Failed to load countries for the country dropdown filter',
                          detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                        }, isAddressListSnackbar: true,
                      }, err?.status);
                      return of(LoadingSpinnerActions.stopLoadingSpinner());
                    }),
                ),
        ),
    );
  });

  upsertColumnNote$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ColumnNotesActions.updateColumnNote,
            LocationsLifecycleActions.locationsComponentDestroyed),
        debounceTime(1000),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type === LocationsLifecycleActions.locationsComponentDestroyed.type ? EMPTY :
            this.apiService.upsertColumnNote(accountId.toString(), action.columnNote)
                .pipe(
                    map((res: ApiResponse<ColumnNote>) =>
                        ColumnNotesActions.updateColumnNoteSuccess({ columnNote: res.result }),
                    ),
                    catchError((err: HttpErrorResponse) => {
                      this.feedbackService.showAlertSnackbar({
                        data: {
                          userFriendlyMessage: `Failed to create/update ${getFullNameForFieldID(action.columnNote.columnName)} column note.`,
                          detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                        }, isAddressListSnackbar: true,
                      }, err?.status);
                      return of(ColumnNotesActions.updateColumnNoteError(
                          { columnNote: action.columnNote }));
                    }),
                ),
        ),
    );
  });

  loadColumnNotes$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ColumnNotesActions.loadColumnNotes,
            LocationsLifecycleActions.locationsComponentInitiated,
            LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type === LocationsLifecycleActions.locationsComponentDestroyed.type ? EMPTY :
            this.apiService.getColumnNotes(accountId.toString())
                .pipe(
                    map((res: ApiResponse<ColumnNote[]>) =>
                        ColumnNotesActions.loadColumnNotesSuccess({ columnNotes: res.result }),
                    ),
                    catchError((err: HttpErrorResponse) => {
                      this.feedbackService.showAlertSnackbar({
                        data: {
                          userFriendlyMessage: 'Loading column notes failed.',
                          detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                        }, isAddressListSnackbar: true,
                      }, err?.status);
                      return of(LoadingSpinnerActions.stopLoadingSpinner());
                    }),
                ),
        ),
    );
  });

  deleteColumnNote$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ColumnNotesActions.deleteColumnNote, LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type === LocationsLifecycleActions.locationsComponentDestroyed.type ? EMPTY :
            this.apiService.deleteColumnNote(
                accountId.toString(),
                action.columnNote.id.toString(),
                action.columnNote.columnName)
                .pipe(
                    map(() => {
                      this.feedbackService.showSuccessSnackbar({ data: { userFriendlyMessage: 'Successfully deleted.' } });
                      // close opened menu by 'faking' a click on the backdrop element
                      document.getElementsByClassName('cdk-overlay-backdrop')[0]?.dispatchEvent(new Event('click'));
                      return ColumnNotesActions.deleteColumnNoteSuccess({ columnNoteName: action.columnNote.columnName });
                    }),
                    catchError((err: HttpErrorResponse) => {
                      this.feedbackService.showAlertSnackbar({
                        data: {
                          userFriendlyMessage: `Failed to delete ${getFullNameForFieldID(action.columnNote.columnName)} column note`,
                          detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                        }, isAddressListSnackbar: true,
                      }, err?.status);
                      return of(ColumnNotesActions.deleteColumnNoteError({ columnNote: action.columnNote }));
                    }),
                ),
        ),
    );
  });

  /**
   * Show snackbar reminding users of that there are some new notes that they might not have seen yet
   */
  showColumnNotesSnackbar$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ColumnNotesActions.loadColumnNotesSuccess),
        tap(action => {
          if (getFromLocalStorage(LocalStorageKeys.COLUMN_NOTES_SNACKBAR_KEY)) {
            // already seen
            return;
          }

          setToLocalStorage(LocalStorageKeys.COLUMN_NOTES_SNACKBAR_KEY,
              'VIEWED');

          const today = startOfDay(new Date());
          const sevenDaysAgo = startOfDay(subDays(today, 7)).getTime();
          // filter column notes that are not older than 7 days from now
          const filteredNotes = action.columnNotes.filter(colNote =>
              colNote.noteCreateTime > sevenDaysAgo ||
              colNote.noteUpdateTime > sevenDaysAgo);

          if (!filteredNotes.length) { // proceed only if there are column notes
            return;
          }

          let snackBarMessage = 'Following columns contain column notes created ' +
              'or updated in the last 7 days: ';

          filteredNotes.forEach((colNote, i) => snackBarMessage +=
              `<strong>${getFullNameForFieldID(colNote.columnName) +
              // add comma if there are several notes and the currently procssed note isn't 
              // the last one
              (filteredNotes.length > 1 && i !== filteredNotes.length - 1 ?
                  ', ' :
                  // add a full stop if there are several notes and the currently procssed note 
                  // is the last one 
                  filteredNotes.length > 1 && i === filteredNotes.length - 1 ?
                      '.' :
                      // don't add anything extra if there is just 1 column note
                      '')}</strong>`);

          this.feedbackService.showInfoSnackbar({
            data: {
              title: 'Column notes',
              userFriendlyMessage: snackBarMessage,
            }, isAddressListSnackbar: true,
          });
        }),
    );
  }, { dispatch: false });


  blacklistFields$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(BlacklistingActions.blacklistFields),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        map(([action, accountId]) => {
          if (!action.locations?.length) {
            return null; // return null - to be filtered out of the stream below
          }
          // fill blacklistedFieldsInfo with fieldName as the key and an array of locationIDs of the locations that blacklist
          // this field as the value; if a fieldName is not blacklisted by any of the selected locations it's empty
          const blacklistedFieldsInfo: Map<string, string[]> = new Map();
          // map entry for every blacklistable field -> assign empty locationStoreCode-Array for each
          const blacklistableFields = getChangeableGmbFields();
          blacklistableFields.forEach((fieldName) => {
            blacklistedFieldsInfo.set(fieldName, []);
          });
          // if the locations have blacklistedFields -> add their locationStoreCodes to the according field name in the map
          action.locations.forEach((location) => {
            location.blackListedFields.forEach((blacklistedFieldOfLocation) => {
              let blacklistedBy = blacklistedFieldsInfo.get(
                  blacklistedFieldOfLocation,
              );
              if (blacklistedBy) {
                blacklistedBy.push(location.storeCode);
                blacklistedFieldsInfo.set(
                    blacklistedFieldOfLocation,
                    blacklistedBy,
                );
              } else {
                // TODO: logging should happen in a tap operator, as it makes the map function impure ..
                // just log a warning in the console here because this error is not necessary to be communicated to the user
                console.warn(
                    'Location with storecode ' +
                    location.storeCode +
                    ' contains a blacklisted field that shouldn\'t be blacklisted: ' +
                    blacklistedFieldOfLocation,
                );
              }
            });
          });
          return {
            locations: action.locations,
            blacklistedFieldsInfo,
            accountId,
            displayedColumns: action.displayedColumns,
          };
        }),
        filter((res) => !!res), // null shall not pass!
        switchMap((res) => this.dialog.open(BlacklistFieldsPopupComponent, {
              data: {
                selectedLocationsCount: res.locations.length,
                blacklistedFieldsInfo: res.blacklistedFieldsInfo,
                displayedColumns: res.displayedColumns,
              },
            }).afterClosed()
                .pipe(map((dialogRes) => ({
                      dialogRes,
                      accountID: res.accountId,
                      locationIds: res.locations.map(l => l.id),
                    })),
                ),
        ),
        filter((res) => !!res.dialogRes?.fieldsToBeBlacklisted?.length ||
            !!res.dialogRes?.fieldsToBeWhitelisted?.length),
        mergeMap((res) => [
          BlacklistingActions.sendBlacklistFieldsRequest({
            accountId: res.accountID,
            locationIds: res.locationIds,
            fieldsToBeBlacklisted: res.dialogRes.fieldsToBeBlacklisted,
            fieldsToBeWhitelisted: res.dialogRes.fieldsToBeWhitelisted,
          }),
          OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.BLACKLISTING }),
        ]),
    );
  });

  sendBlacklistFieldsRequest$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(BlacklistingActions.sendBlacklistFieldsRequest),
        switchMap((action) => this.apiService.blacklistFields(
            action.accountId,
            action.locationIds,
            action.fieldsToBeBlacklisted,
            action.fieldsToBeWhitelisted,
        ).pipe(
            // don't handle this in the outer pipe bc whole subscription would be canceled
            catchError((err: HttpErrorResponse) => {
              return of(err);
            }),
        )),
        map(res => {
          if (res instanceof HttpErrorResponse) {
            return BlacklistingActions.blacklistFieldsError({ err: res });
          }
          return BlacklistingActions.blacklistFieldsSuccess({ locations: res?.result });
        }),
    );
  });

  blacklistFieldsSuccess$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(BlacklistingActions.blacklistFieldsSuccess),
        tap(() => {
          this.feedbackService.showSuccessSnackbar({
            data: {
              userFriendlyMessage: 'Successfully updated blacklisted fields.',
            }, isAddressListSnackbar: true,
          });
        }),
        mergeMap(() => [
          OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.BLACKLISTING }),
          markAllCCListsAsStale(), // mark everything as out-of-date for now
        ]),
    );
  });

  blacklistFieldsError$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(BlacklistingActions.blacklistFieldsError),
        tap(action => {
          this.feedbackService.showAlertSnackbar({
            data: {
              userFriendlyMessage: 'Blacklisting fields failed.',
              detailedInfoFromBE: action.err?.error?.message ? [action.err.error.message] : null,
            }, isAddressListSnackbar: true,
          }, action.err?.status);
        }),
        map(() => OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.BLACKLISTING })),
    );
  });

  refreshGbpConnections$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(RefreshGbpConnectionsActions.refreshGbpConnections),
        filter(action => !!action.locations.length),
        map(action => action.locations.map(l => l.id)),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([locationIds, accountID]) => this.dialog
            .open(GetGbpUpdatesPopupComponent)
            .afterClosed()
            .pipe(
                map((dataToSync) => ({
                  dataToSync,
                  locationIds,
                  accountID,
                })),
            ),
        ),
        filter((res) => !!res.dataToSync?.length),
        mergeMap((res) => [
          RefreshGbpConnectionsActions.sendRefreshGbpConnectionsRequest({
            accountId: res.accountID,
            locationIds: res.locationIds,
            dataToSync: res.dataToSync,
          }),
          OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.GET_UPDATES_FROM_GBP }),
        ]),
    );
  });

  sendRefreshGbpConnectionsRequest$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(RefreshGbpConnectionsActions.sendRefreshGbpConnectionsRequest),
        switchMap((action) => this.apiService
            .refreshGMBConnection(action.accountId, action.locationIds, action.dataToSync)
            .pipe(
                map((apiRes) => ({ apiRes, locations: apiRes?.result })),
                catchError((err: HttpErrorResponse) => {
                  // don't handle this in the outer pipe bc whole subscription would be canceled
                  return of(err);
                }),
            )),
        map(res => {
          if (res instanceof HttpErrorResponse) {
            return RefreshGbpConnectionsActions.refreshGbpConnectionsError({ err: res });
          }
          return RefreshGbpConnectionsActions.refreshGbpConnectionsSuccess({ locations: res.locations });
        }),
    );
  });

  refreshGbpConnectionsSuccess$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(RefreshGbpConnectionsActions.refreshGbpConnectionsSuccess),
        tap(action => {
          this.feedbackService.showSuccessSnackbar({
            data: {
              userFriendlyMessage: action.locations.length +
                  ' ' + (action.locations.length > 1 ? 'locations' : 'location') + ' updated.',
            }, isAddressListSnackbar: true,
          });
        }),
        mergeMap(() => [
          OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.GET_UPDATES_FROM_GBP }),
          markAllCCListsAsStale(), // mark everything as out-of-date for now
        ]),
    );
  });

  refreshGbpConnectionsError$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(RefreshGbpConnectionsActions.refreshGbpConnectionsError),
        tap(action => {
          this.feedbackService.showAlertSnackbar({
            data: {
              userFriendlyMessage: 'Refreshing GBP connection failed.',
              detailedInfoFromBE: action.err?.error?.message ? [action.err.error.message] : null,
            }, isAddressListSnackbar: true,
          }, action.err?.status);
        }),
        map(() => OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.GET_UPDATES_FROM_GBP })),
    );
  });

  editGbpConnections$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(EditGbpConnectionsActions.editGbpConnections),
        filter(action => !!action.location),
        filter(action => (action.command === 'create' && !action.location.foundInMyBusiness) || (action.command === 'remove' && action.location.foundInMyBusiness)),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        mergeMap(([action, accountId]) => {
          const ongoingAction = action.command === 'remove' ? OngoingActionsEnum.REMOVE_GBP_CONNECTION : OngoingActionsEnum.CREATE_GBP_CONNECTION;
          return [
            EditGbpConnectionsActions.sendEditGbpConnectionsRequest({
              accountId,
              locationId: action.location.id,
              command: action.command,
            }),
            OngoingActions.addOngoingAction({ ongoingAction }),
          ];
        }),
    );
  });

  sendEditGbpConnectionsRequest$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(EditGbpConnectionsActions.sendEditGbpConnectionsRequest),
        switchMap(action => this.apiService
            .editGMBConnection(action.accountId, action.locationId, action.command)
            .pipe(
                map((apiRes) => ({ apiRes, command: action.command })),
                catchError((err: HttpErrorResponse) => {
                  // don't handle this in the outer pipe bc whole subscription would be canceled
                  return of({ apiRes: err, command: action.command });
                }),
            )),
        map(res => {
          if (res.apiRes instanceof HttpErrorResponse) {
            return EditGbpConnectionsActions.editGbpConnectionsError({
              err: res.apiRes,
              command: res.command,
            });
          }
          return EditGbpConnectionsActions.editGbpConnectionsSuccess({
            res: res.apiRes,
            command: res.command,
          });
        }),
    );
  });

  editGbpConnectionsSuccess$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(EditGbpConnectionsActions.editGbpConnectionsSuccess),
        tap(action => {

          const showSuccessfulConnectionSnackbar = (message: string) =>
              this.feedbackService.showSuccessSnackbar({
                data: {
                  userFriendlyMessage: message,
                },
                isAddressListSnackbar: true, duration: 3000,
              });

          if (action.command === 'create') {
            if (action.res.status === 'WARNING') {
              this.feedbackService.showWarningSnackbar({
                data: {
                  userFriendlyMessage: 'Location creation on GBP was partially successful.',
                  detailedInfoFromBE: [action.res.message],
                },
                isAddressListSnackbar: true,
              });
            } else {
              showSuccessfulConnectionSnackbar('Successfully created location on GBP.');
            }
          } else {
            showSuccessfulConnectionSnackbar('Successfully disconnected LSC location from GBP location.');
          }

        }),
        mergeMap((action) => {
          const ongoingAction = action.command === 'remove' ? OngoingActionsEnum.REMOVE_GBP_CONNECTION : OngoingActionsEnum.CREATE_GBP_CONNECTION;
          return [
            OngoingActions.removeOngoingAction({ ongoingAction }),
            markAllCCListsAsStale(), // mark everything as out-of-date for now
          ];
        }),
    );
  });

  editGbpConnectionsError$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(EditGbpConnectionsActions.editGbpConnectionsError),
        tap(action => {
          this.feedbackService.showAlertSnackbar({
            data: {
              userFriendlyMessage: 'Could not ' + action.command + ' GBP connection.',
              detailedInfoFromBE: action.err?.error?.message ? [action.err.error.message] : null,
            }, isAddressListSnackbar: true,
          }, action.err?.status);
        }),
        map(action => {
          const ongoingAction = action.command === 'remove' ? OngoingActionsEnum.REMOVE_GBP_CONNECTION : OngoingActionsEnum.CREATE_GBP_CONNECTION;
          return OngoingActions.removeOngoingAction({ ongoingAction });
        }),
    );
  });

  // datasource sync

  getDatasourceSyncDetails$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(SyncActions.getDatasourceSyncDetails,
            LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) =>
            action.type === SyncActions.getDatasourceSyncDetails.type ?
                // get details
                this.apiService.getDatasourceSyncDetails(accountId)
                    .pipe(
                        mergeMap((res) => {
                          const returnActions: any[] = [
                            SyncActions.getDatasourceSyncDetailsSuccess(
                                { datasourceSyncDetails: res?.result })];
                          // also trigger restartDatasourceSync if 'restartDatasourceSync' = true
                          if (action.type === SyncActions.getDatasourceSyncDetails.type &&
                              action?.restartDatasourceSync) {
                            // and if a sync wasn't scheduled or isn't running
                            if (res?.result?.syncStatus !== DatasourceSyncStatus.PENDING &&
                                res?.result?.syncStatus !== DatasourceSyncStatus.RUNNING) {
                              returnActions.push(SyncActions.restartDatasourceSync({}));
                              // show warning otherwise
                            } else {
                              this.feedbackService.showWarningSnackbar({
                                data: {
                                  userFriendlyMessage: 'A datasource sync is already running',
                                },
                                duration: 5000,
                              });
                            }

                          }

                          return returnActions;
                        }),

                        catchError((err) => {
                          // create a Subject that will trigger an action to be dispatched when the user makes a choice
                          const userChoice = new Subject<boolean>(); // true means 'Retry'

                          // show snackbar
                          this.feedbackService.showAlertSnackbarWithRetryFromEffect({
                            userFriendlyMessage: 'Could not get datasource sync details',
                            userChoice,
                            err,
                            // allow retry only if a datasync restart was scheduled
                            // afterward, i.e. user pressed data sync restart button
                            hideRetryButton: !(action.type === SyncActions.getDatasourceSyncDetails.type &&
                                action.restartDatasourceSync),
                          });

                          return userChoice.pipe(switchMap((res) => {
                            const defaultReturnAction$ = of(SyncActions.getDatasourceSyncDetailsError());

                            // retry - add get datasource sync details action
                            if (res) {
                              return defaultReturnAction$
                                  .pipe(mergeMap((getDetailsErrorAction) =>
                                      [getDetailsErrorAction, SyncActions.getDatasourceSyncDetails({ restartDatasourceSync: true })],
                                  ));
                            } else {
                              return defaultReturnAction$;
                            }
                          }));

                        })) :

                // cancel request
                EMPTY),
    );
  });

  restartDatasourceSync$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(SyncActions.restartDatasourceSync,
            LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) =>
            action.type === SyncActions.restartDatasourceSync.type && !action?.triggeredByUser ?
                this.apiService.restartDatasourceSync(accountId)
                    .pipe(
                        map((res) => {
                          this.feedbackService.showSuccessSnackbar({
                            data: {
                              userFriendlyMessage: 'Datasource sync scheduled successfully',
                            },
                          });

                          return SyncActions.restartDatasourceSyncSuccess(
                              { datasourceSyncDetails: res?.result });
                        }),

                        catchError((err) => {
                          // create a Subject that will trigger an action to be dispatched when the user makes a choice
                          const userChoice = new Subject<boolean>(); // true means 'Retry'

                          // show snackbar
                          this.feedbackService.showAlertSnackbarWithRetryFromEffect({
                            userFriendlyMessage: 'There was an error when scheduling the datasource sync',
                            userChoice,
                            err,
                          });

                          return userChoice.pipe(switchMap((res) => {
                            const defaultReturnAction$ = of(SyncActions.restartDatasourceSyncError());

                            // retry - add restart data sync action
                            if (res) {
                              return defaultReturnAction$
                                  .pipe(mergeMap((syncErrorAction) =>
                                      [syncErrorAction, SyncActions.restartDatasourceSync({
                                        // even though this might not have been triggered by user, let's make sure to get sync details first
                                        // and this will lead to a restartDatasourceSync action dispatch if conditions are met
                                        triggeredByUser: true,
                                      })],
                                  ));
                            } else {
                              return defaultReturnAction$;
                            }
                          }));
                        })) :

                // if triggered by user, get sync status instead and schedule restart afterward
                action.type === SyncActions.restartDatasourceSync.type && action?.triggeredByUser ?
                    of(SyncActions.getDatasourceSyncDetails({ restartDatasourceSync: true })) :
                    // cancel request
                    EMPTY),
    );
  });

  // CC sync

  getCCSyncDetails$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(SyncActions.getCCSyncDetails,
            LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) =>
            action.type === SyncActions.getCCSyncDetails.type ?
                // get details
                this.apiService.getCCSyncDetails(accountId)
                    .pipe(
                        map((res) => {
                          return SyncActions.getCCSyncDetailsSuccess({ ccSyncDetails: res?.result });
                        }),
                        catchError((err: HttpErrorResponse) => {
                          // show snackbar
                          this.feedbackService.showAlertSnackbar({
                            data: {
                              userFriendlyMessage: 'Could not get CC sync details',
                            },
                          }, err.status);

                          return of(SyncActions.getCCSyncDetailsError());
                        })) :
                // cancel request
                EMPTY),
    );
  });

  restartCCSync$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(SyncActions.restartCCSync,
            LocationsLifecycleActions.locationsComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) =>
            action.type === SyncActions.restartCCSync.type ?
                // get details
                this.apiService.restartCCSync(accountId)
                    .pipe(
                        map((res) => {
                          return SyncActions.restartCCSyncSuccess({ ccSyncDetails: res?.result });
                        }),
                        catchError((err: HttpErrorResponse) => {
                          // show snackbar
                          this.feedbackService.showAlertSnackbar({
                            data: {
                              userFriendlyMessage: 'Could not restart CC sync',
                            },
                          }, err.status);

                          return of(SyncActions.restartCCSyncError());
                        })) :
                // cancel request
                EMPTY),
    );
  });


  loadLocationsOnAccountSwitchWhenComponentIsActive$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(LocationsLifecycleActions.locationsComponentInitiated),
        switchMap(() =>
            this.actions$.pipe(
                ofType(globalActions.updateSelectedAccount),
                takeUntil(this.actions$.pipe(ofType(LocationsLifecycleActions.locationsComponentDestroyed))),
                mergeMap(() => [
                  LocationsRefreshActions.getEntireLocationList({}),
                  GbpLocationGroupsActions.updateGbpLocationGroups(),
                ]),
            ),
        ),
    );
  });

  getValidationHistory$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(ValidationHistoryActions.getValidationHistory),
        switchMap((action) => {
          const request$: Observable<CoordinateHistory[] | CoordinateHistory> =
              action.lastValidationOnly ?
                  this.apiService.getLastValidationSrc(action.locationId) :
                  this.apiService.getCoordinateHistory(action.locationId);

          return request$.pipe(
              map((res) => {
                const validationData = Array.isArray(res) ?
                    res :
                    res?.source ?
                        [res] :
                        [];
                return ValidationHistoryActions.getValidationHistorySuccess({
                  validationHistory: validationData,
                });
              }),
              catchError((err: HttpErrorResponse) => {
                this.feedbackService.showAlertSnackbar({
                  data: {
                    userFriendlyMessage: '¯\\_(ツ)_/¯ There was an error loading validation data',
                    detailedInfoFromBE: err?.error?.message ? [err.error.message] : [err?.message],
                  },
                }, err.status);
                console.error(err);
                return of(ValidationHistoryActions.getValidationHistoryFail());
              }),
          );
        }),
    );
  });


  syncValidationHistory$ = createEffect(() => {
    return this.store.select(locationListSelectors.selectFocusedLocation).pipe(
        combineLatestWith(this.store.select(mapSelectors.selectShowValidationHistory)),
        concatLatestFrom(() => [
              this.store.select(skeletonSelectors.selectSelectedBottomPanelTab),
              this.store.select(skeletonSelectors.selectBottomPanelActive),
              this.store.select(skeletonSelectors.selectSelectedRightPanelTab),
              this.store.select(skeletonSelectors.selectRightPanelActive),
            ],
        ),
        filter(([[], bottomTab, bottomActive, rightTab, rightActive]) => {
          const isValidationHistoryTabActive = bottomActive && bottomTab === BottomPanelTabs.VALIDATION_HISTORY;
          const isMapTabActive = rightActive && rightTab === RightPanelTabs.MAP;
          // run this effect only if map or history validation history tab is active
          return isValidationHistoryTabActive || isMapTabActive;
        }),
        map(([[location, showHistory], bottomTab, bottomActive]) => {
          const isValidationHistoryTabActive = bottomActive && bottomTab === BottomPanelTabs.VALIDATION_HISTORY;

          return !location ?
              ValidationHistoryActions.resetValidationHistory() :
              ValidationHistoryActions.getValidationHistory({
                locationId: location.id,
                lastValidationOnly: !(showHistory || isValidationHistoryTabActive),
              });
        }),
    );
  });

  getBulkValidationDetails = createEffect(() => {
    return this.actions$.pipe(
        ofType(BulkValidationActions.getBulkValidationDetails),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([, accountId]) =>
            this.apiService.getBulkValidationDetails(accountId).pipe(
                map((res) => BulkValidationActions
                    .getBulkValidationDetailsSuccess({ details: res?.result })),
                catchError((err: HttpErrorResponse) => {
                  this.feedbackService.showAlertSnackbar({
                    data: {
                      userFriendlyMessage: '¯\\_(ツ)_/¯ There was an error loading bulk validation status',
                      detailedInfoFromBE: err?.error?.message ? [err.error.message] : [err?.message],
                    },
                  }, err.status);
                  console.error(err);
                  return of(BulkValidationActions.getBulkValidationDetailsFail());
                })),
        ),
    );
  });

  bulkValidateCoordinates = createEffect(() => {
    return this.actions$.pipe(
        ofType(BulkValidationActions.bulkValidate),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([a, accountId]) =>
            this.apiService.bulkVerifyCoordinates(accountId, a.payload).pipe(
                mergeMap(() => [BulkValidationActions.bulkValidateSuccess(),
                  // refresh status
                  BulkValidationActions.getBulkValidationDetails()]),
                catchError((err: HttpErrorResponse) => {
                  this.feedbackService.showAlertSnackbar({
                    data: {
                      userFriendlyMessage: `¯\\_(ツ)_/¯ There was an error trying to bulk-${a.payload.action.toLowerCase()} locations`,
                      detailedInfoFromBE: err?.error?.message ? [err.error.message] : [err?.message],
                    },
                  }, err.status);
                  console.error(err);
                  return of(BulkValidationActions.bulkValidateFail());
                })),
        ),
    );
  });
}
